'use client';

import { useState, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Testimonial {
  id: number;
  name: string;
  role: string;
  organization: string;
  content: string;
  rating: number;
  image: string;
  alt: string;
}

interface TestimonialsProps {
  className?: string;
}

const Testimonials = ({ className = '' }: TestimonialsProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState<number>(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const testimonials: Testimonial[] = [
  {
    id: 1,
    name: 'Dr. Robert Anderson',
    role: 'Director of Surgical Services',
    organization: 'Metropolitan Medical Center',
    content: 'AIVA Medical Supply has been instrumental in upgrading our surgical imaging capabilities. Their C-Arm systems arrived with complete compliance documentation, and their technical team provided exceptional installation support. The equipment quality and their responsive service have significantly improved our operational efficiency.',
    rating: 5,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ba51e044-1763296048176.png",
    alt: 'Middle-aged Caucasian man with gray hair in white medical coat with stethoscope smiling in hospital corridor'
  },
  {
    id: 2,
    name: 'Jennifer Martinez',
    role: 'Chief Procurement Officer',
    organization: 'Regional Healthcare Network',
    content: 'Working with AIVA has transformed our equipment procurement process. Their team understands the complexities of healthcare purchasing, providing detailed specifications, compliance certifications, and competitive pricing. The transparency and professionalism they bring to every transaction builds confidence in our partnership.',
    rating: 5,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_18ca2627c-1763293853689.png",
    alt: 'Professional Hispanic woman with long dark hair in navy business suit smiling confidently in modern office'
  },
  {
    id: 3,
    name: 'Dr. Michael Thompson',
    role: 'Biomedical Engineering Manager',
    organization: 'University Hospital System',
    content: 'The technical expertise at AIVA is unmatched. From equipment selection to maintenance protocols, their biomedical engineers provide invaluable guidance. Their commitment to quality assurance and ongoing support ensures our medical devices maintain peak performance, directly impacting patient care quality.',
    rating: 5,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_137523df9-1763296110727.png",
    alt: 'African American man in white lab coat with safety glasses holding tablet in medical equipment room'
  }];


  useEffect(() => {
    if (!isHydrated) return;

    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 8000);

    return () => clearInterval(interval);
  }, [isHydrated, testimonials.length]);

  if (!isHydrated) {
    return (
      <section className={`py-20 bg-gradient-to-b from-surface to-white ${className}`}>
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="h-96 bg-white rounded-2xl animate-pulse"></div>
        </div>
      </section>);

  }

  const handlePrevious = () => {
    setActiveTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const handleNext = () => {
    setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  return (
    <section className={`py-20 bg-gradient-to-b from-surface to-white ${className}`}>
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-2 bg-brand-accent/10 text-brand-accent rounded-full text-sm font-semibold mb-4">
            Client Success Stories
          </div>
          <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
            Trusted by Healthcare Leaders
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Hear from hospital administrators, surgical center directors, and biomedical engineers who have experienced the AIVA difference in medical equipment supply and support.
          </p>
        </div>

        <div className="relative max-w-5xl mx-auto">
          <div className="bg-white rounded-2xl shadow-brand-lg overflow-hidden border border-border">
            <div className="grid lg:grid-cols-5 gap-0">
              <div className="lg:col-span-2 relative h-64 lg:h-auto">
                <AppImage
                  src={testimonials[activeTestimonial].image}
                  alt={testimonials[activeTestimonial].alt}
                  className="w-full h-full object-cover" />

                <div className="absolute inset-0 bg-gradient-to-t lg:bg-gradient-to-r from-primary/80 to-transparent"></div>
                <div className="absolute bottom-6 left-6 right-6 lg:bottom-8 lg:left-8">
                  <div className="flex items-center space-x-1 mb-3">
                    {[...Array(testimonials[activeTestimonial].rating)].map((_, i) =>
                    <Icon key={i} name="StarIcon" size={20} className="text-yellow-400" variant="solid" />
                    )}
                  </div>
                  <h3 className="text-xl font-bold text-white mb-1">
                    {testimonials[activeTestimonial].name}
                  </h3>
                  <p className="text-sm text-white/90">{testimonials[activeTestimonial].role}</p>
                  <p className="text-xs text-white/80">{testimonials[activeTestimonial].organization}</p>
                </div>
              </div>

              <div className="lg:col-span-3 p-8 lg:p-12 flex flex-col justify-center">
                <Icon name="ChatBubbleLeftRightIcon" size={48} className="text-primary/20 mb-6" />
                <p className="text-lg text-text-primary leading-relaxed mb-8 italic">
                  "{testimonials[activeTestimonial].content}"
                </p>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    {testimonials.map((_, index) =>
                    <button
                      key={index}
                      onClick={() => setActiveTestimonial(index)}
                      className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      activeTestimonial === index ? 'bg-primary w-8' : 'bg-border'}`
                      }
                      aria-label={`Go to testimonial ${index + 1}`}>
                    </button>
                    )}
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={handlePrevious}
                      className="w-10 h-10 bg-surface hover:bg-primary hover:text-white rounded-full flex items-center justify-center transition-all duration-300 border border-border"
                      aria-label="Previous testimonial">

                      <Icon name="ChevronLeftIcon" size={20} />
                    </button>
                    <button
                      onClick={handleNext}
                      className="w-10 h-10 bg-surface hover:bg-primary hover:text-white rounded-full flex items-center justify-center transition-all duration-300 border border-border"
                      aria-label="Next testimonial">

                      <Icon name="ChevronRightIcon" size={20} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12 grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-brand text-center border border-border">
              <p className="text-4xl font-bold text-primary mb-2">98%</p>
              <p className="text-sm font-semibold text-text-primary mb-1">Customer Satisfaction</p>
              <p className="text-xs text-text-secondary">Based on annual surveys</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-brand text-center border border-border">
              <p className="text-4xl font-bold text-accent mb-2">4.9/5</p>
              <p className="text-sm font-semibold text-text-primary mb-1">Average Rating</p>
              <p className="text-xs text-text-secondary">From verified customers</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-brand text-center border border-border">
              <p className="text-4xl font-bold text-brand-accent mb-2">92%</p>
              <p className="text-sm font-semibold text-text-primary mb-1">Repeat Business</p>
              <p className="text-xs text-text-secondary">Long-term partnerships</p>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default Testimonials;